import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
const ESRSSectorStandards = lazy(() => import("../../frameworks/ESRSSectorStandards"));

const API = process.env.REACT_APP_BACKEND_URL;

const C={bg:"#050a14",surface:"#0c1220",card:"#111c30",border:"#1a2744",borderHi:"#2a3a5c",text:"#e2e8f0",muted:"#6b7a96",dim:"#384660",blue:"#3b82f6",amber:"#f59e0b",emerald:"#10b981",purple:"#8b5cf6",cyan:"#06b6d4",red:"#ef4444",pink:"#ec4899",teal:"#14b8a6",orange:"#f97316",lime:"#84cc16",white:"#fff",indigo:"#6366f1",rose:"#f43f5e"};

const ESRS_META = {
  E1:{l:"Climate Change",p:"E",c:C.emerald,desc:"GHG emissions, climate adaptation, energy"},
  E2:{l:"Pollution",p:"E",c:C.orange,desc:"Air, water, soil pollution prevention"},
  E3:{l:"Water & Marine",p:"E",c:C.cyan,desc:"Water consumption, marine resources"},
  E4:{l:"Biodiversity",p:"E",c:C.lime,desc:"Ecosystems, species, land use"},
  E5:{l:"Resource Use",p:"E",c:C.teal,desc:"Circular economy, waste, materials"},
  S1:{l:"Own Workforce",p:"S",c:C.blue,desc:"Working conditions, equal treatment, H&S"},
  S2:{l:"Value Chain Workers",p:"S",c:C.indigo,desc:"Workers in supply & distribution"},
  S3:{l:"Communities",p:"S",c:C.pink,desc:"Affected communities, indigenous rights"},
  S4:{l:"Consumers",p:"S",c:C.rose,desc:"Product safety, privacy, inclusion"},
  G1:{l:"Business Conduct",p:"G",c:C.purple,desc:"Anti-corruption, lobbying, payments"},
};

const FW_T={E1:{gri:["GRI 302","GRI 305"],issb:["IFRS S2"],cdp:["C6 Emissions","C8 Energy"],tcfd:["MET-a","STRAT-b"]},E2:{gri:["GRI 305-7","GRI 306"],issb:[],cdp:[],tcfd:[]},E3:{gri:["GRI 303"],issb:[],cdp:["W1-W4 Water"],tcfd:[]},E4:{gri:["GRI 304"],issb:[],cdp:["F1-F4 Forests"],tcfd:[]},E5:{gri:["GRI 301","GRI 306"],issb:[],cdp:[],tcfd:[]},S1:{gri:["GRI 401-405","GRI 403"],issb:["IFRS S1 ¶25"],cdp:[],tcfd:[]},S2:{gri:["GRI 407-409","GRI 414"],issb:[],cdp:[],tcfd:[]},S3:{gri:["GRI 411","GRI 413"],issb:[],cdp:[],tcfd:[]},S4:{gri:["GRI 416","GRI 418"],issb:[],cdp:[],tcfd:[]},G1:{gri:["GRI 205-207"],issb:["IFRS S1 ¶26"],cdp:[],tcfd:["GOV-a","GOV-b"]}};

const CROSS_CUTTING = [
  {code:"ESRS 1",name:"General Requirements",reqs:["Basis for preparation","Time horizons (short/medium/long)","Value chain boundary definition","Materiality assessment methodology","Connected information across standards","Incorporation by reference"]},
  {code:"ESRS 2",name:"General Disclosures",reqs:["BP-1: Basis for preparation","BP-2: Disclosures in relation to specific circumstances","GOV-1: Role of admin, management & supervisory bodies","GOV-2: Information provided to governance bodies","GOV-3: Integration of sustainability in incentive schemes","GOV-4: Due diligence statement","GOV-5: Risk management & internal controls","SBM-1: Strategy, business model & value chain","SBM-2: Interests & views of stakeholders","SBM-3: Material impacts, risks & opportunities","IRO-1: Description of materiality assessment process","IRO-2: Disclosure requirements covered by materiality assessment","MDR-P: Policies adopted","MDR-A: Actions & resources","MDR-M: Metrics","MDR-T: Targets"]},
];

const SECTORS = [
  {id:"og",name:"Oil & Gas",standards:["E1","E2","E3","E4","S1","S2","S3","G1"],active:false},
  {id:"en",name:"Energy",standards:["E1","E2","E3","S1","G1"],active:false},
  {id:"mn",name:"Mining & Quarrying",standards:["E1","E2","E3","E4","E5","S1","S2","S3","G1"],active:false},
  {id:"rd",name:"Road Transport",standards:["E1","E2","S1","S4"],active:false},
  {id:"ag",name:"Agriculture & Farming",standards:["E1","E2","E3","E4","E5","S1","S2"],active:false},
  {id:"tx",name:"Textiles & Apparel",standards:["E2","E3","E5","S1","S2","S4"],active:false},
  {id:"fo",name:"Food & Beverage",standards:["E1","E3","E4","E5","S1","S2","S4"],active:false},
  {id:"fc",name:"Financial Institutions",standards:["E1","S1","S4","G1"],active:false},
];

function getToken() {
  return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
}
async function apiFetch(path) {
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
}

const Pl=({color:cl,children,small,onClick,active,glow})=><span onClick={onClick} style={{display:"inline-flex",alignItems:"center",gap:3,padding:small?"1px 6px":"3px 9px",borderRadius:99,fontSize:small?8:10,fontWeight:600,background:active?cl+"22":cl+"10",color:cl,border:`1px solid ${active?cl+"66":cl+"22"}`,cursor:onClick?"pointer":"default",transition:"all .2s",boxShadow:glow?`0 0 8px ${cl}33`:"none"}}>{children}</span>;
const Bar=({v,mx,cl,h=5})=><div style={{width:"100%",height:h,borderRadius:99,background:C.border,overflow:"hidden"}}><div style={{width:Math.min((v/mx)*100,100)+"%",height:"100%",borderRadius:99,background:`linear-gradient(90deg,${cl}88,${cl})`,transition:"width .4s"}}/></div>;

export default function CSRDIntegratedPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("cross");
  const [activation, setActivation] = useState({});
  const [loading, setLoading] = useState(true);
  const [sectors, setSectors] = useState(SECTORS);
  const [expandedStd, setExpandedStd] = useState(null);
  const [expandedCross, setExpandedCross] = useState(null);

  useEffect(() => {
    apiFetch("/api/v1/materiality/standard-activation")
      .then(data => { setActivation(data.standards || {}); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const activeCount = useMemo(() => Object.values(activation).filter(a => a.active).length, [activation]);
  const totalIROs = useMemo(() => Object.values(activation).reduce((s, a) => s + (a.total || 0), 0), [activation]);
  const materialIROs = useMemo(() => Object.values(activation).reduce((s, a) => s + (a.material || 0), 0), [activation]);

  const tabs = [
    {id:"cross",l:"Cross-Cutting",desc:"ESRS 1-2 (Always Active)"},
    {id:"topical",l:"Topical (E1-G1)",desc:"Driven by Materiality"},
    {id:"sector",l:"Sector-Specific",desc:"Industry Standards"},
  ];

  if (loading) return <div style={{background:C.bg,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",color:C.muted,fontSize:14}}>Loading CSRD/ESRS data...</div>;

  // ═══ CROSS-CUTTING VIEW ══════════════════════════════════════════
  const CrossCuttingView = () => (
    <div data-testid="cross-cutting-view">
      <div style={{marginBottom:12,padding:"12px 16px",borderRadius:8,background:C.emerald+"06",border:`1px solid ${C.emerald}22`}}>
        <div style={{fontSize:12,fontWeight:700,color:C.emerald,marginBottom:4}}>Mandatory — Always Active</div>
        <div style={{fontSize:10,color:C.muted}}>ESRS 1 and ESRS 2 apply to ALL undertakings regardless of materiality assessment outcome. These cross-cutting standards define the general requirements and disclosures that frame every topical standard.</div>
      </div>
      {CROSS_CUTTING.map((std, idx) => (
        <div key={std.code} data-testid={`cross-${std.code.replace(/\s/g,"-")}`} style={{marginBottom:8}}>
          <div onClick={() => setExpandedCross(expandedCross === idx ? null : idx)} style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",borderRadius:expandedCross===idx?"8px 8px 0 0":8,background:C.card,border:`1px solid ${C.emerald}22`,cursor:"pointer"}}>
            <div style={{width:36,height:36,borderRadius:6,background:C.emerald+"12",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:C.emerald}}>{std.code.split(" ")[1]}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:11,fontWeight:700,color:C.text}}>{std.code} — {std.name}</div>
              <div style={{fontSize:9,color:C.muted}}>{std.reqs.length} disclosure requirements</div>
            </div>
            <Pl color={C.emerald} small glow>ACTIVE</Pl>
            <span style={{fontSize:9,color:C.dim,transform:expandedCross===idx?"rotate(180deg)":"rotate(0)",transition:"transform .2s"}}>▼</span>
          </div>
          {expandedCross === idx && (
            <div style={{padding:"10px 14px",border:`1px solid ${C.emerald}22`,borderTop:"none",borderRadius:"0 0 8px 8px",background:C.surface}}>
              {std.reqs.map((req, j) => (
                <div key={j} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 8px",borderRadius:4,background:C.card,marginBottom:3,fontSize:9}}>
                  <span style={{color:C.emerald,fontSize:10}}>✓</span>
                  <span style={{color:C.text}}>{req}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // ═══ TOPICAL VIEW ════════════════════════════════════════════════
  const TopicalView = () => (
    <div data-testid="topical-view">
      <div style={{marginBottom:12,padding:"12px 16px",borderRadius:8,background:C.amber+"06",border:`1px solid ${C.amber}22`}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
          <span style={{fontSize:12,fontWeight:700,color:C.amber}}>Activation from Materiality Intelligence</span>
          <Pl color={C.amber} small glow>{activeCount}/10 active</Pl>
        </div>
        <div style={{fontSize:10,color:C.muted}}>Each standard below activates when at least one IRO tagged to it is scored as material. Activation is determined upstream in the <span onClick={() => navigate("/materiality-intelligence/activation")} style={{color:C.blue,cursor:"pointer",textDecoration:"underline"}}>Materiality Intelligence module</span>.</div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:8}}>
        {Object.entries(ESRS_META).map(([code, info]) => {
          const a = activation[code] || { active: false, total: 0, material: 0, material_iros: [], triggers: {} };
          const triggers = FW_T[code] || {};
          const allFW = [...(triggers.gri||[]),...(triggers.issb||[]),...(triggers.cdp||[]),...(triggers.tcfd||[])];
          const isExpanded = expandedStd === code;

          return (
            <div key={code} data-testid={`topical-${code}`} style={{borderRadius:8,background:a.active?info.c+"06":C.card,border:`1px solid ${a.active?info.c+"33":C.border}`,opacity:a.active?1:0.35,transition:"all .3s"}}>
              <div onClick={() => setExpandedStd(isExpanded ? null : code)} style={{padding:"12px 14px",cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                  <div style={{width:32,height:32,borderRadius:6,background:info.c+"15",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:info.c}}>{code}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:700,color:info.c}}>{info.l}</div>
                    <div style={{fontSize:8,color:C.muted}}>{info.desc}</div>
                  </div>
                  {a.active
                    ? <Pl color={info.c} small glow>ACTIVE</Pl>
                    : <Pl color={C.dim} small>INACTIVE</Pl>
                  }
                </div>
                <Bar v={a.material} mx={a.total || 1} cl={info.c} h={4} />
                <div style={{fontSize:8,color:C.muted,marginTop:3}}>{a.material} material of {a.total} IROs</div>
              </div>

              {isExpanded && (
                <div style={{padding:"0 14px 12px"}}>
                  {a.active && a.material_iros?.length > 0 && (
                    <div style={{marginBottom:8}}>
                      <div style={{fontSize:8,fontWeight:700,color:info.c,marginBottom:4}}>TRIGGERING IROs</div>
                      {a.material_iros.map(iro => (
                        <div key={iro.id} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 8px",borderRadius:4,background:C.card,marginBottom:2,fontSize:9,border:`1px solid ${C.border}`}}>
                          <span style={{color:iro.type==="impact"?C.red:iro.type==="risk"?C.amber:C.emerald}}>{iro.type==="impact"?"●":iro.type==="risk"?"▲":"◆"}</span>
                          <span style={{flex:1,color:C.text}}>{iro.name}</span>
                          <span style={{fontSize:8,color:C.red,fontWeight:700}}>I:{iro.impact_score}</span>
                          <span style={{fontSize:8,color:C.blue,fontWeight:700}}>F:{iro.financial_score}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {a.active && allFW.length > 0 && (
                    <div style={{marginBottom:8}}>
                      <div style={{fontSize:8,fontWeight:700,color:C.purple,marginBottom:4}}>VOLUNTARY FRAMEWORK TRIGGERS</div>
                      <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                        {triggers.gri?.map(g => <Pl key={g} color={C.emerald} small>{g}</Pl>)}
                        {triggers.issb?.map(g => <Pl key={g} color={C.blue} small>{g}</Pl>)}
                        {triggers.cdp?.map(g => <Pl key={g} color={C.amber} small>{g}</Pl>)}
                        {triggers.tcfd?.map(g => <Pl key={g} color={C.cyan} small>{g}</Pl>)}
                      </div>
                    </div>
                  )}

                  <div style={{textAlign:"center",paddingTop:4,borderTop:`1px solid ${C.border}`}}>
                    <button data-testid={`go-materiality-${code}`} onClick={() => navigate("/materiality-intelligence")} style={{padding:"4px 12px",borderRadius:4,border:`1px solid ${C.blue}44`,background:C.blue+"12",color:C.blue,fontSize:8,cursor:"pointer",fontWeight:600}}>
                      {a.active ? "← Managed in Materiality Intelligence → IRO Register" : "← Add IROs in Materiality Intelligence"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ═══ SECTOR-SPECIFIC VIEW ════════════════════════════════════════
  // (Replaced by ESRSSectorStandards component)

  // ═══ RENDER ═════════════════════════════════════════════════════
  return (
    <div data-testid="csrd-integrated-page" style={{background:C.bg,minHeight:"100vh",color:C.text,fontFamily:"'Segoe UI',system-ui,sans-serif"}}>
      <div style={{padding:"10px 18px 6px",borderBottom:`1px solid ${C.border}`,position:"sticky",top:0,zIndex:10,background:C.bg+"f0",backdropFilter:"blur(12px)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
          <div style={{width:26,height:26,borderRadius:6,background:`linear-gradient(135deg,${C.teal},${C.blue})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:C.white}}>D</div>
          <span style={{fontSize:13,fontWeight:700}}>DisclosureOS</span>
          <span style={{color:C.dim}}>/</span>
          <span style={{fontSize:13,fontWeight:700,color:C.blue}}>CSRD / ESRS</span>
          <div style={{flex:1}}/>
          <Pl color={C.emerald} small glow>2 cross-cutting</Pl>
          <Pl color={C.amber} small>{activeCount}/10 topical</Pl>
          <Pl color={C.purple} small>{sectors.filter(s=>s.active).length} sectors</Pl>
          <Pl color={C.cyan} small>{totalIROs} IROs</Pl>
        </div>
        <div data-testid="csrd-tabs" style={{display:"flex",gap:3}}>
          {tabs.map(t => (
            <button key={t.id} data-testid={`csrd-tab-${t.id}`} onClick={() => setTab(t.id)} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:5,fontSize:9,fontWeight:600,cursor:"pointer",border:`1px solid ${tab===t.id?C.blue:C.border}`,background:tab===t.id?C.blue+"15":"transparent",color:tab===t.id?C.blue:C.muted}}>
              {t.l}
              <span style={{fontSize:7,color:C.dim}}>— {t.desc}</span>
            </button>
          ))}
        </div>
      </div>
      <div style={{padding:"12px 18px 40px",maxWidth:1400,margin:"0 auto"}}>
        {tab === "cross" && <CrossCuttingView />}
        {tab === "topical" && <TopicalView />}
        {tab === "sector" && <Suspense fallback={<div style={{color:C.muted,padding:20}}>Loading Sector Standards...</div>}><ESRSSectorStandards /></Suspense>}
      </div>
      <div style={{textAlign:"center",padding:"10px 18px 16px",borderTop:`1px solid ${C.border}`}}>
        <div style={{fontSize:10,color:C.muted}}>CSRD/ESRS consumes materiality outputs. <span onClick={() => navigate("/materiality-intelligence")} style={{color:C.blue,cursor:"pointer",textDecoration:"underline"}}>Manage assessments in Materiality Intelligence →</span></div>
      </div>
    </div>
  );
}
